import React from 'react'
import { Container, Col, Row, Button } from 'react-bootstrap'
import { DateTime } from 'luxon'
import { Listing, SectionStyles } from '../ui/Listing/Listing'
import { Loading } from '../ui/Loading/Loading'
import { handleExportXlsx } from '../../utilities/export'
import { minutesToHours, roundToTwo } from '../../utilities/format'
import WipCount from './wipCount'

import { Filters } from '../../screens/wip/wip'
import { Grouping } from '../../constants/grouping'
import {
	WipClientWithJobsAndLatestInvoice,
	WipData,
	WipJobTypeWithClients,
	WipJobWithTime,
	WipTimeWithoutJob,
	ClientGroup,
	Staff,
} from '../../../../back-end/utilities/apiDefinitions'

import fileIcon from '../../images/icons/search.svg'
import NewInvoiceButton from '../invoice/newInvoiceButton'

interface FCJETopListingRow {
	id: string
	type: 'group' | 'client'
	groupName?: string
	clientName?: string
	lastInvoiced?: string
	agedTime: number
	currentTime: number
	totalTime: number
	agedValue: number
	currentValue: number
	value: number
}

type FCJEListingData = (FCJETopListingRow & { children: (FCJETopListingRow & { child: React.ReactNode })[] })[]

interface FCJEChildListingRow {
	id: string
	type: 'job' | 'time' | 'cost' | 'estimate'
	jobNumberName?: string
	date?: string
	employeeName?: string
	taskName?: string
	note?: string
	agedTime: number
	currentTime: number
	totalTime: number
	agedValue: number
	currentValue: number
	value: number
}

type FCJEChildListingData = (FCJEChildListingRow & { children: FCJEChildListingRow[] })[]

const isGroup = (item: FCJETopListingRow | FJCJTopListingRow) => item.type === 'group'

const isJob = (item: FCJEChildListingRow | FJCJChildListingRow) => item.type === 'job'

const isClient = (item: FCJETopListingRow | FJCJChildListingRow) => item.type === 'client'

type AllowedTimeReduceKeys = keyof Pick<
	WipTimeWithoutJob,
	'minutes' | 'remainingDollars' | 'dollars' | 'agedTime' | 'currentTime' | 'agedValue' | 'currentValue'
>

const reduceTimeArray = (timeArray: WipTimeWithoutJob[], key: AllowedTimeReduceKeys) => timeArray.reduce((acc, t) => acc + t[key], 0)

const reduceJobArray = (jobArray: WipJobWithTime[], key: AllowedTimeReduceKeys) => jobArray.reduce((acc, j) => acc + reduceTimeArray(j.timeItems, key), 0)

const reduceClientArray = (clientArray: WipClientWithJobsAndLatestInvoice[], key: AllowedTimeReduceKeys) =>
	clientArray.reduce((acc, c) => acc + reduceJobArray(c.jobs, key), 0)

const reduceJobTypeArray = (jobTypeArray: WipJobTypeWithClients[], key: AllowedTimeReduceKeys) =>
	jobTypeArray.reduce((acc, jt) => acc + reduceClientArray(jt.clients, key), 0)

const mapFCJEJobTime = (jobsWithTime: WipJobWithTime[]): FCJEChildListingData => {
	return jobsWithTime
		.sort((a, b) => (a.number < b.number ? -1 : 1))
		.map((j) => ({
			id: j.jobID,
			type: 'job',
			jobNumberName: `${j.number} - ${j.name}`,
			agedTime: reduceTimeArray(j.timeItems, 'agedTime'),
			currentTime: reduceTimeArray(j.timeItems, 'currentTime'),
			totalTime: reduceTimeArray(j.timeItems, 'minutes'),
			agedValue: reduceTimeArray(j.timeItems, 'agedValue'),
			currentValue: reduceTimeArray(j.timeItems, 'currentValue'),
			value: reduceTimeArray(j.timeItems, 'remainingDollars'),
			children: j.timeItems
				.sort((a, b) => (a.date < b.date ? -1 : 1))
				.map((t) => ({
					id: t.timeID || t.costID || t.estimateID || '',
					type: 'time',
					jobName: '',
					date: t.date,
					employeeName: t.staff.name,
					taskName: t.task.name,
					note: t.note || undefined,
					agedTime: t.agedTime,
					currentTime: t.currentTime,
					totalTime: t.minutes,
					agedValue: t.agedValue,
					currentValue: t.currentValue,
					value: t.remainingDollars,
				})),
		}))
}

const FCJEChildListing = (props: { data: WipJobWithTime[]; isLoading: boolean }) => (
	<div className="py-2 pr-2">
		<Listing<FCJEChildListingRow>
			list={mapFCJEJobTime(props.data)}
			columns={[
				{
					value: (item) => item.jobNumberName || '',
					render: (item) => <div>{item.jobNumberName}</div>,
					colSpan: (item) => (isJob(item) ? 5 : 1),
					width: '100px',
					showHeader: true,
					headerText: 'Jobs',
					sortColumnName: 'jobName',
				},
				{
					value: (item) => item.date || '',
					render: (item) => <div>{DateTime.fromISO(item.date || '').toFormat('dd/MM/yyyy')}</div>,
					hidden: (item) => isJob(item),
					width: '100px',
					showHeader: true,
					headerText: 'Date',
					sortColumnName: 'date',
				},
				{
					value: (item) => item.employeeName || '',
					render: (item) => <div>{item.employeeName}</div>,
					hidden: (item) => isJob(item),
					showHeader: true,
					headerText: 'Employee',
					sortColumnName: 'employeeName',
				},
				{
					value: (item) => item.taskName || '',
					render: (item) => <div>{item.taskName}</div>,
					hidden: (item) => isJob(item),
					showHeader: true,
					headerText: 'Task',
					sortColumnName: 'taskName',
				},
				{
					value: (item) => item.note || '',
					render: (item) => <div>{item.note}</div>,
					hidden: (item) => isJob(item),
					showHeader: true,
					headerText: 'Comments',
					sortColumnName: 'comments',
				},
				{
					value: (item) => item.totalTime.toString(),
					render: (item) => <div className="text-end">{minutesToHours(item.totalTime)}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Total Hrs',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'totalTime',
				},
				{
					value: (item) => item.agedValue.toString(),
					render: (item) => <div className="text-end">{isJob(item) || item.agedValue !== 0 ? roundToTwo(item.agedValue).toLocaleString() : '-'}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Aged Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'agedValue',
				},
				{
					value: (item) => item.currentValue.toString(),
					render: (item) => (
						<div className="text-end">{isJob(item) || item.currentValue !== 0 ? roundToTwo(item.currentValue).toLocaleString() : '-'}</div>
					),
					width: '160px',
					showHeader: true,
					headerText: 'Current Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'currentValue',
				},
				{
					value: (item) => item.value.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.value).toLocaleString()}</div>,
					width: 'calc(160px - 0.5rem)',
					showHeader: true,
					headerText: 'Total Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'value',
				},
			]}
			name="WIP Record"
			namePlural="WIP Records"
			getIDFunc={(item) => item.id}
			defaultSort={{ column: 'date', order: 'ASC' }}
			isLoading={props.isLoading}
			paginationControls="none"
			listingBodyOnly
			sectionStyles={listingStyle}
			borderRadius
			boxShadow
			autoExpand
			headerPadRow
		/>
	</div>
)

const mapWipDataFCJE = (wipData: WipData, isLoading: boolean): FCJEListingData => {
	if (wipData.grouping === Grouping.fcje.value) {
		return wipData.familyGroups
			.sort((a, b) => (a.name < b.name ? -1 : 1))
			.map((fg) => ({
				id: fg.clientGroupID,
				type: 'group',
				groupName: fg.name,
				clientName: '',
				lastInvoiced: '',
				agedTime: reduceClientArray(fg.clients, 'agedTime'),
				currentTime: reduceClientArray(fg.clients, 'currentTime'),
				totalTime: reduceClientArray(fg.clients, 'minutes'),
				agedValue: reduceClientArray(fg.clients, 'agedValue'),
				currentValue: reduceClientArray(fg.clients, 'currentValue'),
				value: reduceClientArray(fg.clients, 'remainingDollars'),
				children: fg.clients
					.sort((a, b) => (a.name < b.name ? -1 : 1))
					.map((c) => ({
						id: c.clientID,
						type: 'client',
						groupName: '',
						clientName: c.name,
						lastInvoiced: c.newestInvoiceDate ? DateTime.fromISO(c.newestInvoiceDate).toFormat('dd LLL yyyy') : undefined,
						agedTime: reduceJobArray(c.jobs, 'agedTime'),
						currentTime: reduceJobArray(c.jobs, 'currentTime'),
						totalTime: reduceJobArray(c.jobs, 'minutes'),
						agedValue: reduceJobArray(c.jobs, 'agedValue'),
						currentValue: reduceJobArray(c.jobs, 'currentValue'),
						value: reduceJobArray(c.jobs, 'remainingDollars'),
						child: <FCJEChildListing data={c.jobs} isLoading={isLoading} />,
					})),
			}))
	}

	return []
}

interface FJCJTopListingRow {
	id: string
	type: 'group' | 'type'
	groupName?: string
	typeName?: string | null
	agedTime: number
	currentTime: number
	totalTime: number
	agedValue: number
	currentValue: number
	value: number
}

type FJCJListingData = (FJCJTopListingRow & { children: (FJCJTopListingRow & { child: React.ReactNode })[] })[]

interface FJCJChildListingRow {
	id: string
	type: 'client' | 'job' | 'time'
	clientName?: string
	jobNumberName?: string
	date?: string
	employeeName?: string
	taskName?: string
	note?: string
	lastInvoiced?: string
	agedTime: number
	currentTime: number
	totalTime: number
	agedValue: number
	currentValue: number
	value: number
}

type FJCJChildListingData = (FJCJChildListingRow & { children: (FJCJChildListingRow & { children: FJCJChildListingRow[] })[] })[]

const mapFJCJJobTime = (clients: WipClientWithJobsAndLatestInvoice[]): FJCJChildListingData => {
	return clients
		.sort((a, b) => (a.name < b.name ? -1 : 1))
		.map((c) => ({
			id: c.clientID,
			type: 'client',
			clientName: c.name,
			agedTime: reduceJobArray(c.jobs, 'agedTime'),
			currentTime: reduceJobArray(c.jobs, 'currentTime'),
			totalTime: reduceJobArray(c.jobs, 'minutes'),
			agedValue: reduceJobArray(c.jobs, 'agedValue'),
			currentValue: reduceJobArray(c.jobs, 'currentValue'),
			value: reduceJobArray(c.jobs, 'remainingDollars'),
			lastInvoiced: c.newestInvoiceDate ? DateTime.fromISO(c.newestInvoiceDate).toFormat('dd LLL YYYY') : undefined,
			children: c.jobs
				.sort((a, b) => (a.number < b.number ? -1 : 1))
				.map((j) => ({
					id: j.jobID,
					type: 'job',
					jobNumberName: `${j.number} - ${j.name}`,
					agedTime: reduceTimeArray(j.timeItems, 'agedTime'),
					currentTime: reduceTimeArray(j.timeItems, 'currentTime'),
					totalTime: reduceTimeArray(j.timeItems, 'minutes'),
					agedValue: reduceTimeArray(j.timeItems, 'agedValue'),
					currentValue: reduceTimeArray(j.timeItems, 'currentValue'),
					value: reduceTimeArray(j.timeItems, 'remainingDollars'),
					children: j.timeItems
						.sort((a, b) => (a.date < b.date ? -1 : 1))
						.map((t) => ({
							id: t.timeID || t.costID || t.estimateID || '',
							type: 'time',
							jobName: '',
							date: t.date,
							employeeName: t.staff.name,
							taskName: t.task.name,
							note: t.note || undefined,
							agedTime: t.agedTime,
							currentTime: t.currentTime,
							totalTime: t.minutes,
							agedValue: t.agedValue,
							currentValue: t.currentValue,
							value: t.remainingDollars,
						})),
				})),
		}))
}

const FJCJChildListing = (props: { data: WipClientWithJobsAndLatestInvoice[]; isLoading: boolean }) => {
	const wipChildDataFJCJ = React.useMemo(() => mapFJCJJobTime(props.data), [props.data, props.isLoading])

	return (
		<div className="py-2 pr-2">
			<Listing<FJCJChildListingRow>
				list={wipChildDataFJCJ}
				initialExpanded={wipChildDataFJCJ.reduce((expanded, wip) => ({ ...expanded, [wip.id]: true }), {})}
				columns={[
					{
						value: (item) => item.clientName || '',
						render: (item) => <div>{item.clientName}</div>,
						colSpan: (item) => (isClient(item) ? 6 : 1),
						hidden: (item) => isJob(item),
						width: '120px',
						showHeader: true,
						headerText: 'Client Name',
						sortColumnName: 'clientName',
					},
					{
						value: (item) => item.jobNumberName || '',
						render: (item) => <div>{item.jobNumberName}</div>,
						colSpan: (item) => (isJob(item) ? 5 : 1),
						hidden: (item) => isClient(item),
						width: '80px',
						showHeader: true,
						headerText: 'Jobs',
						sortColumnName: 'jobName',
					},
					{
						value: (item) => item.date || '',
						render: (item) => <div>{DateTime.fromISO(item.date || '').toFormat('dd/MM/yyyy')}</div>,
						hidden: (item) => isClient(item) || isJob(item),
						width: '100px',
						showHeader: true,
						headerText: 'Date',
						sortColumnName: 'date',
					},
					{
						value: (item) => item.employeeName || '',
						render: (item) => <div>{item.employeeName}</div>,
						hidden: (item) => isClient(item) || isJob(item),
						showHeader: true,
						headerText: 'Employee',
						sortColumnName: 'employeeName',
					},
					{
						value: (item) => item.taskName || '',
						render: (item) => <div>{item.taskName}</div>,
						hidden: (item) => isClient(item) || isJob(item),
						showHeader: true,
						headerText: 'Task',
						sortColumnName: 'taskName',
					},
					{
						value: (item) => item.note || '',
						render: (item) => <div>{item.note}</div>,
						hidden: (item) => isClient(item) || isJob(item),
						showHeader: true,
						headerText: 'Comments',
						sortColumnName: 'comments',
					},
					{
						value: (item) => item.totalTime.toString(),
						render: (item) => <div className="text-end">{minutesToHours(item.totalTime)}</div>,
						width: '160px',
						showHeader: true,
						headerText: 'Total Hrs',
						headerClass: 'text-end',
						sortJustify: 'end',
						sortColumnName: 'totalTime',
					},
					{
						value: (item) => item.agedValue.toString(),
						render: (item) => (
							<div className="text-end">{isJob(item) || item.agedValue !== 0 ? roundToTwo(item.agedValue).toLocaleString() : '-'}</div>
						),
						width: '160px',
						showHeader: true,
						headerText: 'Aged Value',
						headerClass: 'text-end',
						sortJustify: 'end',
						sortColumnName: 'agedValue',
					},
					{
						value: (item) => item.currentValue.toString(),
						render: (item) => (
							<div className="text-end">{isJob(item) || item.currentValue !== 0 ? roundToTwo(item.currentValue).toLocaleString() : '-'}</div>
						),
						width: '160px',
						showHeader: true,
						headerText: 'Current Value',
						headerClass: 'text-end',
						sortJustify: 'end',
						sortColumnName: 'currentValue',
					},
					{
						value: (item) => item.value.toString(),
						render: (item) => <div className="text-end">{roundToTwo(item.value).toLocaleString()}</div>,
						width: 'calc(160px - 0.5rem)',
						showHeader: true,
						headerText: 'Total Value',
						headerClass: 'text-end',
						sortJustify: 'end',
						sortColumnName: 'value',
					},
				]}
				name="WIP Record"
				namePlural="WIP Records"
				getIDFunc={(item) => item.id}
				defaultSort={{ column: 'date', order: 'ASC' }}
				isLoading={props.isLoading}
				paginationControls="none"
				listingBodyOnly
				sectionStyles={listingStyle}
				borderRadius
				boxShadow
				headerPadRow
			/>
		</div>
	)
}

const mapWipDataFJCJ = (wipData: WipData, isLoading: boolean): FJCJListingData => {
	if (wipData.grouping === Grouping.fjcj.value) {
		return wipData.familyGroups
			.sort((a, b) => (a.name < b.name ? -1 : 1))
			.map((fg) => ({
				id: fg.clientGroupID,
				type: 'group',
				groupName: fg.name,
				typeName: '',
				agedTime: reduceJobTypeArray(fg.jobTypes, 'agedTime'),
				currentTime: reduceJobTypeArray(fg.jobTypes, 'currentTime'),
				totalTime: reduceJobTypeArray(fg.jobTypes, 'minutes'),
				agedValue: reduceJobTypeArray(fg.jobTypes, 'agedValue'),
				currentValue: reduceJobTypeArray(fg.jobTypes, 'currentValue'),
				value: reduceJobTypeArray(fg.jobTypes, 'remainingDollars'),
				children: fg.jobTypes
					.sort((a, b) => ((a.type || '') < (b.type || '') ? -1 : 1))
					.map((jt) => ({
						id: `${fg.clientGroupID}-${jt.type || ''}`,
						type: 'type',
						groupName: '',
						typeName: jt.type,
						agedTime: reduceClientArray(jt.clients, 'agedTime'),
						currentTime: reduceClientArray(jt.clients, 'currentTime'),
						totalTime: reduceClientArray(jt.clients, 'minutes'),
						agedValue: reduceClientArray(jt.clients, 'agedValue'),
						currentValue: reduceClientArray(jt.clients, 'currentValue'),
						value: reduceClientArray(jt.clients, 'remainingDollars'),
						child: <FJCJChildListing data={jt.clients} isLoading={isLoading} />,
					})),
			}))
	}

	return []
}

const listingStyle: SectionStyles = {
	trDepth0: { background: '#8AA6FF40' },
	trDepth1: { background: '#FFF' },
	trDepth2: { background: '#FFF' },
}

interface ListingScreenProps {
	isLoading: boolean
	wipData: WipData | null
	allStaff: Staff[]
	selectedJobLabel?: string
	familyGroups: ClientGroup[]
	BackButton: React.ReactNode
	filters: Filters
}

const ListingScreen = (props: ListingScreenProps) => {
	const wipDataFCJE = React.useMemo(() => {
		if (props.wipData) {
			if (props.wipData.grouping === 'fcje') {
				return mapWipDataFCJE(props.wipData, props.isLoading)
			}
		}
		return []
	}, [props.wipData, props.isLoading])

	const wipDataFJCJ = React.useMemo(() => {
		if (props.wipData) {
			if (props.wipData.grouping === 'fjcj') {
				return mapWipDataFJCJ(props.wipData, props.isLoading)
			}
		}
		return []
	}, [props.wipData, props.isLoading])

	const handleDownload = () => {
		const filename = `WIP_List_Ending_${props.filters.endDate.toFormat('dd-MM-yyyy')}.xlsx`
		if (props.wipData?.grouping === 'fcje') {
			handleExportXlsx(
				filename,
				props.wipData.familyGroups.flatMap((fg) =>
					fg.clients.flatMap((c) =>
						c.jobs.flatMap((j) =>
							j.timeItems.flatMap((t) => ({
								Group: fg.name,
								'Client Name': c.name,
								'Job Number': j.number,
								Job: j.name,
								Date: DateTime.fromISO(t.date).toFormat('dd/MM/yyyy'),
								Employee: t.staff.name,
								Task: t.task.name,
								Comments: t.note || '',
								'Aged Time (minutes)': t.agedTime,
								'Current Time (minutes)': t.currentTime,
								'Total Time (minutes)': t.minutes,
								'Aged Value': t.agedValue,
								'Current Value': t.currentValue,
								'Total Value': t.remainingDollars,
							}))
						)
					)
				)
			)
		} else if (props.wipData?.grouping === 'fjcj') {
			handleExportXlsx(
				filename,
				props.wipData.familyGroups.flatMap((fg) =>
					fg.jobTypes.flatMap((jt) =>
						jt.clients.flatMap((c) =>
							c.jobs.flatMap((j) =>
								j.timeItems.flatMap((t) => ({
									Group: fg.name,
									'Job Category': jt.type || '',
									'Client Name': c.name,
									'Job Number': j.number,
									Job: j.name,
									Date: DateTime.fromISO(t.date).toFormat('dd/MM/yyyy'),
									Employee: t.staff.name,
									Task: t.task.name,
									Comments: t.note || '',
									'Aged Time (minutes)': t.agedTime,
									'Current Time (minutes)': t.currentTime,
									'Total Time (minutes)': t.minutes,
									'Aged Value': t.agedValue,
									'Current Value': t.currentValue,
									'Total Value': t.remainingDollars,
								}))
							)
						)
					)
				)
			)
		}
	}

	const FCJEListing = () => (
		<Listing<FCJETopListingRow>
			list={wipDataFCJE}
			initialExpanded={wipDataFCJE.reduce((expanded, wip) => ({ ...expanded, [wip.id]: true }), {})}
			columns={[
				{
					value: (item) => item.groupName || '',
					render: (item) => <div className="listing-group">{item.groupName}</div>,
					hidden: (item) => isClient(item),
					colSpan: (item) => (isGroup(item) ? 3 : 1),
					width: '160px',
					showHeader: true,
					headerText: 'Group',
					sortColumnName: 'groupName',
				},
				{
					value: (item) => item.clientName || '',
					render: (item) => <div>{item.clientName}</div>,
					hidden: (item) => isGroup(item),
					showHeader: true,
					headerText: 'Client Name',
					sortColumnName: 'clientName',
				},
				{
					value: (item) => item.lastInvoiced || '',
					render: (item) => <div className="text-end">{item.lastInvoiced}</div>,
					hidden: (item) => isGroup(item),
					showHeader: true,
					headerText: 'Last Invoiced',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'lastInvoiced',
					width: '160px',
				},
				{
					value: (item) => item.totalTime.toString(),
					render: (item) => <div className="text-end">{minutesToHours(item.totalTime)}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Total Hrs',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'totalTime',
				},
				{
					value: (item) => item.agedValue.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.agedValue).toLocaleString()}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Aged Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'agedValue',
				},
				{
					value: (item) => item.currentValue.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.currentValue).toLocaleString()}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Current Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'currentValue',
				},
				{
					value: (item) => item.value.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.value).toLocaleString()}</div>,
					width: 'calc(160px - 0.5rem)',
					showHeader: true,
					headerText: 'Total Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'value',
				},
			]}
			customButtons={[<NewInvoiceButton key="add" />]}
			name="WIP Record"
			namePlural="WIP Records"
			getIDFunc={(item) => item.id}
			defaultSort={{ column: 'groupName', order: 'ASC' }}
			isLoading={props.isLoading}
			paginationControls="both"
			sectionStyles={listingStyle}
			borderRadius
			boxShadow
			smallPagination
			headerPadRow
			hideDisplayCount
		/>
	)

	const FJCJListing = () => (
		<Listing<FJCJTopListingRow>
			list={wipDataFJCJ}
			initialExpanded={wipDataFJCJ.reduce((expanded, wip) => ({ ...expanded, [wip.id]: true }), {})}
			columns={[
				{
					value: (item) => item.groupName || '',
					render: (item) => <div className="listing-group">{item.groupName}</div>,
					hidden: (item) => item.groupName === '',
					colSpan: (item) => (isGroup(item) ? 2 : 1),
					width: '160px',
					showHeader: true,
					headerText: 'Group',
					sortColumnName: 'groupName',
				},
				{
					value: (item) => item.typeName || '',
					render: (item) => <div>{item.typeName}</div>,
					hidden: (item) => isGroup(item),
					showHeader: true,
					headerText: 'Job Category',
					sortColumnName: 'typeName',
				},
				{
					value: (item) => item.totalTime.toString(),
					render: (item) => <div className="text-end">{minutesToHours(item.totalTime)}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Total Hrs',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'totalTime',
				},
				{
					value: (item) => item.agedValue.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.agedValue).toLocaleString()}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Aged Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'agedValue',
				},
				{
					value: (item) => item.currentValue.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.currentValue).toLocaleString()}</div>,
					width: '160px',
					showHeader: true,
					headerText: 'Current Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'currentValue',
				},
				{
					value: (item) => item.value.toString(),
					render: (item) => <div className="text-end">{roundToTwo(item.value).toLocaleString()}</div>,
					width: 'calc(160px - 0.5rem)',
					showHeader: true,
					headerText: 'Total Value',
					headerClass: 'text-end',
					sortJustify: 'end',
					sortColumnName: 'value',
				},
			]}
			customButtons={[<NewInvoiceButton key="add" />]}
			name="WIP Record"
			namePlural="WIP Records"
			getIDFunc={(item) => item.id}
			defaultSort={{ column: 'groupName', order: 'ASC' }}
			isLoading={props.isLoading}
			paginationControls="both"
			sectionStyles={listingStyle}
			borderRadius
			boxShadow
			smallPagination
			headerPadRow
			hideDisplayCount
		/>
	)

	const ListingBody = () => {
		if (props.isLoading) {
			return (
				<div className="center-flex p-5">
					<Loading show />
				</div>
			)
		}

		if (!props.wipData) {
			return null
		}

		if (props.wipData.grouping === 'fcje') {
			return <FCJEListing />
		}

		if (props.wipData.grouping === 'fjcj') {
			return <FJCJListing />
		}

		return null
	}

	return (
		<Container fluid>
			<Row className="background-light align-items-center py-3">
				<Col>
					<Row className="pb-2">
						<Col sm="auto">{props.BackButton}</Col>
					</Row>
					<Row>
						<Col sm="auto">
							<WipCount wipData={props.wipData} filters={props.filters} allStaff={props.allStaff} selectedJobLabel={props.selectedJobLabel} />
						</Col>
					</Row>
				</Col>
				<Col />
				<Col sm="auto">
					<Button variant="primary" size="sm" onClick={() => handleDownload()}>
						<div className="d-flex align-items-center justify-content-between">
							<img src={fileIcon} className="mr-2" />
							<span>Export XLSX</span>
						</div>
					</Button>
				</Col>
			</Row>
			<Row>
				<Col>
					<ListingBody />
				</Col>
			</Row>
		</Container>
	)
}
export default ListingScreen
